import React from 'react';
import { cssNum } from '@/helpers';
import { useRef, useEffect, useState, useLayoutEffect } from 'react';
import styled from 'styled-components';

interface ScrollViewProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	rightOffset?: number;
	leftOffset?: number;
	height?: number | string;
	isNavigation?: boolean; // When enabled it will override the window.scrollY value
	onScrollStart?: () => void;
	onScrollRemove?: () => void;
}

function ScrollView(
	{ children, rightOffset = 0, leftOffset = 0, isNavigation, onScrollStart, onScrollRemove, ...rest }: ScrollViewProps,
	ref: React.Ref<HTMLDivElement>,
) {
	const contentRef = useRef<HTMLDivElement>(null);
	const verticalThumbRef = useRef<HTMLDivElement>(null);
	const horizontalThumbRef = useRef<HTMLDivElement>(null);
	// SECTION - Thumb state
	const [thumbHeight, setThumbHeight] = useState(0);
	const [thumbWidth, setThumbWidth] = useState(0);
	// SECTION - Scrollbar state
	const [showVerticalScrollbar, setShowVerticalScrollbar] = useState(false);
	const [showHorizontalScrollbar, setShowHorizontalScrollbar] = useState(false);
	// SECTION - Shadow state
	const [showLeftShadow, setShowLeftShadow] = useState(false);
	const [showRightShadow, setShowRightShadow] = useState(false);
	// SECTION - Dragging state
	const [isDraggingVertical, setIsDraggingVertical] = useState(false);
	const [isDraggingHorizontal, setIsDraggingHorizontal] = useState(false);
	const [dragStartY, setDragStartY] = useState(0);
	const [scrollStartY, setScrollStartY] = useState(0);
	const [dragStartX, setDragStartX] = useState(0);
	const [scrollStartX, setScrollStartX] = useState(0);

	const updateScrollbars = () => {
		const contentElement = contentRef.current;
		if (contentElement) {
			const { scrollTop, scrollHeight, clientHeight, scrollLeft, scrollWidth, clientWidth } = contentElement;

			const showVerticalScrollbar = scrollHeight > clientHeight;
			const showHorizontalScrollbar = scrollWidth > clientWidth;

			setShowVerticalScrollbar(showVerticalScrollbar);
			setShowHorizontalScrollbar(showHorizontalScrollbar);

			if (showVerticalScrollbar) {
				const thumbHeight = (clientHeight / scrollHeight) * clientHeight;
				setThumbHeight(thumbHeight);
				if (verticalThumbRef.current) {
					verticalThumbRef.current.style.top = `${(scrollTop / scrollHeight) * clientHeight}px`;
				}
			}

			if (showHorizontalScrollbar) {
				const offsets = leftOffset + rightOffset;
				const thumbWidth = (clientWidth / scrollWidth) * clientWidth;
				setThumbWidth(thumbWidth);
				if (horizontalThumbRef.current) {
					horizontalThumbRef.current.style.left = `${(scrollLeft / scrollWidth) * clientWidth - offsets}px`;
				}
			}

			// If isNavigation is enabled, it will override the window scroll position values
			if (isNavigation) {
				window.scrollTo(0, scrollTop);
			}

			setShowLeftShadow(scrollLeft > 0);
			setShowRightShadow(scrollLeft < scrollWidth - clientWidth);
		}
	};

	useLayoutEffect(() => {
		const contentElement = contentRef.current;

		const resizeObserver = new ResizeObserver(() => {
			updateScrollbars();
		});

		if (contentElement) {
			resizeObserver.observe(contentElement);
		}

		return () => {
			if (contentElement) {
				resizeObserver.unobserve(contentElement);
			}
		};
	}, [leftOffset, rightOffset, isNavigation]);

	useEffect(() => {
		if (isNavigation && contentRef.current) {
			// Restore scroll position from session storage if available
			const savedScrollPosition = sessionStorage.getItem('scrollPosition');
			if (savedScrollPosition) {
				contentRef.current.scrollTop = Number.parseInt(savedScrollPosition, 10);
			}
		}
	}, [isNavigation]);

	useEffect(() => {
		if (isNavigation) {
			const handleBeforeUnload = () => {
				if (contentRef.current) {
					const scrollPosition = contentRef.current.scrollTop;
					sessionStorage.setItem('scrollPosition', scrollPosition.toString());
				}
			};

			// Save scroll position before page unloads or user navigates away
			window.addEventListener('beforeunload', handleBeforeUnload);

			return () => {
				window.removeEventListener('beforeunload', handleBeforeUnload);
			};
		}
	}, [isNavigation]);

	useEffect(() => {
		const contentElement = contentRef.current;
		if (contentElement) {
			contentElement.addEventListener('scroll', updateScrollbars);
			updateScrollbars();
			return () => contentElement.removeEventListener('scroll', updateScrollbars);
		}
	}, [leftOffset, rightOffset, isNavigation]);

	useEffect(() => {
		const handleMouseMove = (e: MouseEvent) => {
			if (isDraggingVertical && contentRef.current) {
				const deltaY = e.clientY - dragStartY;
				const newScrollTop = scrollStartY + deltaY / (thumbHeight / contentRef.current.clientHeight);
				contentRef.current.scrollTop = newScrollTop;
			}

			if (isDraggingHorizontal && contentRef.current) {
				const deltaX = e.clientX - dragStartX;
				const newScrollLeft = scrollStartX + deltaX / (thumbWidth / contentRef.current.clientWidth);
				contentRef.current.scrollLeft = newScrollLeft;
			}
		};

		const handleMouseUp = () => {
			setIsDraggingVertical(false);
			setIsDraggingHorizontal(false);
		};

		document.addEventListener('mousemove', handleMouseMove);
		document.addEventListener('mouseup', handleMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [isDraggingVertical, isDraggingHorizontal, dragStartY, scrollStartY, dragStartX, scrollStartX, thumbHeight, thumbWidth]);

	const startDraggingVertical = (e: React.MouseEvent) => {
		if (contentRef.current) {
			setIsDraggingVertical(true);
			setDragStartY(e.clientY);
			setScrollStartY(contentRef.current.scrollTop);
		}
	};

	const startDraggingHorizontal = (e: React.MouseEvent) => {
		if (contentRef.current) {
			setIsDraggingHorizontal(true);
			setDragStartX(e.clientX);
			setScrollStartX(contentRef.current.scrollLeft);
		}
	};

	const offsets: Offesets = {
		$leftOffset: leftOffset,
		$rightOffset: rightOffset,
	};

	// Expose scrollTop to parent
	useEffect(() => {
		if (ref && typeof ref !== 'function' && contentRef.current) {
			ref.current = contentRef.current;
		}
	}, [ref]);

	// Handle onScrollRemove callback
	useEffect(() => {
		if (!onScrollRemove) return;

		let timeoutId: NodeJS.Timeout | null = null;

		const handleScroll = () => {
			if (contentRef.current) {
				const scrollTop = contentRef.current.scrollTop;
				if (scrollTop === 0) {
					if (timeoutId) clearTimeout(timeoutId);
					timeoutId = setTimeout(() => {
						if (onScrollRemove) onScrollRemove();
					}, 100);
				} else if (timeoutId) clearTimeout(timeoutId);
			}
		};

		const contentElement = contentRef.current;
		if (contentElement) {
			contentElement.addEventListener('scroll', handleScroll);
			return () => contentElement.removeEventListener('scroll', handleScroll);
		}
	}, [onScrollRemove]);

	// Handle onScrollStart callback
	useEffect(() => {
		if (!onScrollStart) return;

		const handleScroll = () => {
			if (contentRef.current) {
				const scrollTop = contentRef.current.scrollTop;
				if (scrollTop > 0 && onScrollStart) {
					onScrollStart();
				}
			}
		};

		const contentElement = contentRef.current;
		if (contentElement) {
			contentElement.addEventListener('scroll', handleScroll);
			return () => contentElement.removeEventListener('scroll', handleScroll);
		}
	}, [onScrollStart]);

	return (
		<div className="scroll-view-container">
			<ScrollContainer {...rest}>
				<Content
					ref={(r: any) => {
						contentRef.current = r;
						if (ref) {
							if (typeof ref === 'function') {
								ref(r);
							} else {
								ref.current = r;
							}
						}
					}}
				>
					{children}
				</Content>
				{showVerticalScrollbar && (
					<ScrollbarTrack {...offsets}>
						<ScrollbarThumb
							ref={verticalThumbRef}
							style={{ height: `${thumbHeight}px` }}
							onMouseDown={startDraggingVertical}
							{...offsets}
						/>
					</ScrollbarTrack>
				)}
				{showHorizontalScrollbar && (
					<HorizontalScrollbarWrapper {...offsets}>
						<HorizontalScrollbarTrack {...offsets}>
							<HorizontalScrollbarThumb
								ref={horizontalThumbRef}
								style={{ width: `${thumbWidth}px` }}
								onMouseDown={startDraggingHorizontal}
								{...offsets}
							/>
						</HorizontalScrollbarTrack>
					</HorizontalScrollbarWrapper>
				)}
				{showLeftShadow && (
					<Shadow
						$left
						$offset={leftOffset}
					/>
				)}
				{showRightShadow && (
					<Shadow
						$right
						$offset={rightOffset}
					/>
				)}
			</ScrollContainer>
		</div>
	);
}

export default React.forwardRef(ScrollView);

const ScrollContainer = styled.div<{ height?: number | string } & React.HTMLAttributes<HTMLDivElement>>`
	position: relative;
	width: 100%;
	height: ${({ height }) => (height ? cssNum(height) : '100%')};
	overflow: hidden; /* Hide default scrollbar */
`;

const Content = styled.div`
	width: 100%;
	height: 100%;
	overflow: auto; /* Allow scrolling */
	display: flex;
	flex-direction: column;
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
	&::-webkit-scrollbar {
		display: none; /* Safari and Chrome */
	}
`;

type Offesets = { $leftOffset?: number; $rightOffset?: number };
const ScrollbarTrack = styled.div<Offesets>`
	z-index: 5;
	position: absolute;
	right: ${({ $rightOffset }) => ($rightOffset || 0) + 2}px;
	top: 0;
	bottom: 0;
	width: 8px;
	background: rgba(0, 0, 0, 0.05);
	border-radius: 65px;
`;

const ScrollbarThumb = styled.div<Offesets>`
	z-index: 1;
	position: absolute;
	width: 8px;
	// border: 1px solid red;
	background: rgba(136, 136, 136, 0.2);
	border-radius: 65px;
	cursor: pointer;
	user-select: none;
	&:hover {
		background: rgba(136, 136, 136, 0.3);
	}
	min-height: 20px;
`;

const HorizontalScrollbarWrapper = styled.div<Offesets>`
	z-index: 5;
	position: absolute;
	left: 0px;
	bottom: 0px;
	padding-left: ${({ $leftOffset }) => $leftOffset || 0}px;
	padding-right: ${({ $rightOffset }) => $rightOffset || 0}px;
	height: 8px;
	width: 100%;
`;

const HorizontalScrollbarTrack = styled.div<Offesets>`
	position: relative;
	height: 8px;
	background: rgba(0, 0, 0, 0.05);
	border-radius: 6px;
`;

const HorizontalScrollbarThumb = styled.div<Offesets>`
	position: absolute;
	bottom: 0px;
	height: 8px;
	background: rgba(136, 136, 136, 0.2);
	border-radius: 6px;
	cursor: pointer;
	user-select: none;
	&:hover {
		background: rgba(136, 136, 136, 0.3);
	}
`;

const Shadow = styled.div<{ $left?: boolean; $right?: boolean; $offset?: number }>`
	z-index: 2;
	position: absolute;
	top: 0;
	bottom: 0;
	width: 1px;
	box-shadow: ${({ $left }) => ($left ? '-2px' : '2px')} 0 6px 4px rgba(0, 0, 0, 0.06);
	pointer-events: none;
	${({ $left, $offset }) => $left && `left: ${$offset}px;`}
	${({ $right, $offset }) => $right && `right: ${$offset}px;`}
`;
